import React, {
	useState,
	useEffect,
	useCallback,
	useMemo,
	useRef,
} from 'react';
import {
	Col,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';

import { useLoggedUser } from '../Hooks/useUserHooks';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { getEstablishmentList } from '../../slices/thunks';
import * as Yup from 'yup';
import OSelect from '../OElements/OSelect';
import { setEstablishmentActive } from '../../slices/auth/profile/reducer';

// Cache para establishments para evitar requests duplicadas
const establishmentCache = {
	data: null,
	isLoaded: false,
	isLoading: false,
};

const SelectEstablishmentActive = ({ establishmentId }) => {
	const { userProfile } = useLoggedUser();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	//Dropdown Toggle
	const [isProfileDropdown, setIsProfileDropdown] = useState(false);
	const toggleProfileDropdown = () => {
		setIsProfileDropdown(!isProfileDropdown);
	};

	const selectLayoutState = state => state.Profile;
	const userProfileData = createSelector(selectLayoutState, state => ({
		user: state.user,
		establishmentActive: state.establishmentActive,
	}));
	const { user, establishmentActive } = useSelector(userProfileData);
	const [permissionType, setPermissionType] = useState(null);
	const [establishmentList, setEstablishmentList] = useState([]);
	const [establishmentListFiltered, setEstablishmentListFiltered] = useState(
		[]
	);

	const [selectedEstablishmentActive, setSelectedEstablishmentActive] =
		useState(null);
	const handleSelectedEstablishmentActive = optionSelected => {
		setSelectedEstablishmentActive(optionSelected);
	};

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			establishment_default_id: '',
		},
		validationSchema: Yup.object({
			establishment_default_id: Yup.string().nullable(),
		}),
		onSubmit: values => {
			let establishmentSelectedFounded;
			if (permissionType == 1) {
				establishmentSelectedFounded = establishmentList.find(
					establishmentItem =>
						establishmentItem.item._id === values.establishment_default_id
				);
			}
			if (permissionType === 2 || permissionType === 3) {
				establishmentSelectedFounded = establishmentListFiltered.find(
					establishmentItem =>
						establishmentItem.item._id === values.establishment_default_id
				);
			}
			dispatch(
				setEstablishmentActive(
					establishmentSelectedFounded
						? establishmentSelectedFounded
						: null
				)
			);
		},
	});

	// Optimized function with caching to avoid duplicate requests
	const loadingEstablishments = useCallback(async () => {
		// Return cached data if available
		if (establishmentCache.isLoaded && establishmentCache.data) {
			setEstablishmentList(establishmentCache.data);
			return;
		}

		// Prevent multiple simultaneous requests
		if (establishmentCache.isLoading) {
			return;
		}

		establishmentCache.isLoading = true;

		try {
			const response = await dispatch(
				getEstablishmentList({ optimized: true })
			).unwrap();

			let establishments = response.map(establishment => {
				return {
					value: establishment._id,
					label: establishment.name,
					item: establishment,
				};
			});

			// Agregar el item "Todos" con estructura compatible
			establishments = [
				{
					value: 'all',
					label: 'Todos',
					item: { _id: 'all', code: 'all', name: 'Todos' },
				},
				...establishments.sort((a, b) => a.label.localeCompare(b.label)),
			];

			// Cache the result
			establishmentCache.data = establishments;
			establishmentCache.isLoaded = true;

			setEstablishmentList(establishments);
		} catch (err) {
			console.error('Error al cargar establecimientos:', err);
		} finally {
			establishmentCache.isLoading = false;
		}
	}, [dispatch]);

	// Effect to handle of establishment list filtered by types
	// when selection of establishment types and permission type changed.
	useEffect(() => {
		if (permissionType === 2) {
			setEstablishmentListFiltered(
				establishmentList
					.filter(establishmentItem =>
						user.establishment_type_ids.includes(
							establishmentItem.item.establishment_type_id
						)
					)
					.sort((a, b) => a.label.localeCompare(b.label))
			);
		}
		if (permissionType === 3) {
			setEstablishmentListFiltered(
				establishmentList
					.filter(establishmentItem =>
						user.establishment_ids.includes(establishmentItem.item._id)
					)
					.sort((a, b) => a.label.localeCompare(b.label))
			);
		}
	}, [permissionType, establishmentList]);

	useEffect(() => {
		if (user) {
			setPermissionType(1);
			if (user?.permission_by_establishment_type) {
				setPermissionType(2);
			}
			if (user?.permission_by_establishment) {
				setPermissionType(3);
			}
		}
	}, [user]);

	useEffect(() => {
		loadingEstablishments();
	}, []);

	useEffect(() => {
		validation.handleSubmit();
	}, [selectedEstablishmentActive]);

	useEffect(() => {
		if (permissionType > 1 && establishmentListFiltered?.length === 1) {
			dispatch(setEstablishmentActive(establishmentListFiltered[0]));
		}
	}, [permissionType, establishmentListFiltered]);

	if (permissionType > 1 && establishmentListFiltered?.length === 1) {
		return (
			<React.Fragment>
				<h4
					className='m-0 p-0 fw-bold'
					style={{ color: 'var(--vz-header-item-sub-color)' }}
				>
					{establishmentListFiltered[0].label.toUpperCase()}
				</h4>
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			<OSelect
				key={
					`establishment_default_id` +
						user.establishment_default_id +
						permissionType +
						establishmentList +
						establishmentListFiltered || ''
				}
				label=''
				id='establishment_default_id'
				name='establishment_default_id'
				value={
					establishmentId ? establishmentId : user.establishment_default_id
				}
				itemsSelect={() => {
					if (permissionType == 1) return establishmentList;
					if (permissionType === 2 || permissionType === 3)
						return establishmentListFiltered;
					return [];
				}}
				onSelect={handleSelectedEstablishmentActive}
				validation={validation}
			/>
		</React.Fragment>
	);
};

export default SelectEstablishmentActive;
